import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Save, Send, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import api, { formatApiError } from "../lib/api";
import { FormProvider, useForm, STEP_TITLES } from "../context/FormContext";
import ProgressSidebar from "../components/form/ProgressSidebar";
import MobileNavDrawer from "../components/form/MobileNavDrawer";
import PrivacyBanner from "../components/form/PrivacyBanner";
import {
  Step1Intro, Step2Locatore, Step3Conduttore, Step4Zona, Step5Catasto,
  Step6Contratto, Step7Valutazione, Step8Superfici, Step9Maggiorazioni,
  Step10Documents, Step11Result, Step12Declaration,
} from "../components/form/FormSteps";
import { checkEmail, isValidEmail } from "../lib/emailValidation";

function isEmail(s) {
  return isValidEmail(s);
}

function validateStep(step, data, files, calculation) {
  switch (step) {
    case 1: {
      const r = data.richiedente || {};
      if (!r.tipo) return "Indica chi sta compilando la pratica (locatore o professionista incaricato)";
      if (r.tipo === "agenzia") {
        const chk = checkEmail(r.email);
        if (!chk.ok) return `Email di riferimento del professionista incaricato — ${chk.reason}`;
        if (!r.agenzia_nome?.trim()) return "Nome del professionista incaricato obbligatorio";
        if (!r.agenzia_referente?.trim()) return "Nome referente obbligatorio";
      }
      return null;
    }
    case 2: {
      const list = data.locatori && data.locatori.length ? data.locatori : [data.locatore || {}];
      for (let i = 0; i < list.length; i++) {
        const p = list[i] || {};
        const suffix = list.length > 1 ? ` (locatore n. ${i + 1})` : "";
        const nome = (p.nome || "").trim();
        if (!nome) return `Nome e Cognome del locatore obbligatorio${suffix}`;
        // Richiedi sia nome che cognome (min. 2 parole di almeno 2 caratteri ciascuna)
        const parts = nome.split(/\s+/).filter((t) => t.length >= 2);
        if (parts.length < 2) return `Inserisci sia il Nome sia il Cognome del locatore${suffix}`;
        const chk = checkEmail(p.email);
        if (!chk.ok) return `Email del locatore${suffix} — ${chk.reason}`;
      }
      return null;
    }
    case 3: {
      const list = data.conduttori && data.conduttori.length ? data.conduttori : [data.conduttore || {}];
      const locatori = Array.isArray(data.locatori) && data.locatori.length
        ? data.locatori
        : [data.locatore || {}];
      const locatoreEmails = new Set(
        locatori
          .map((l) => String(l?.email || "").trim().toLowerCase())
          .filter(Boolean)
      );
      // Email del conduttore obbligatoria SOLO quando la pratica è compilata
      // dal locatore (nello Step 1 "Sono il Locatore"). Se la compila il
      // Professionista incaricato, l'email del conduttore resta facoltativa
      // perché le firme vengono acquisite in sede.
      const richiedenteTipo = String(data?.richiedente?.tipo || "").toLowerCase();
      const emailRequired = richiedenteTipo === "locatore";
      const seenCondEmails = new Set();
      for (let i = 0; i < list.length; i++) {
        const p = list[i] || {};
        const suffix = list.length > 1 ? ` (conduttore n. ${i + 1})` : "";
        const nome = (p.nome || "").trim();
        if (!nome) return `Nome e Cognome del conduttore obbligatorio${suffix}`;
        const parts = nome.split(/\s+/).filter((t) => t.length >= 2);
        if (parts.length < 2) return `Inserisci sia il Nome sia il Cognome del conduttore${suffix}`;
        const email = String(p.email || "").trim().toLowerCase();
        if (emailRequired && !email)
          return `Email del conduttore obbligatoria${suffix}`;
        if (email) {
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
            return `Email del conduttore non valida${suffix}`;
          if (locatoreEmails.has(email))
            return `L'email del conduttore deve essere diversa da quella del locatore${suffix}`;
          if (seenCondEmails.has(email))
            return `L'email del conduttore è già usata da un altro conduttore${suffix}`;
          seenCondEmails.add(email);
        }
      }
      return null;
    }
    case 4:
      if (!data.zona) return "Inserisci un indirizzo Milano valido per rilevare la zona";
      if (!data.immobile.via?.trim()) return "Via obbligatoria";
      if (!data.immobile.civico?.trim()) return "Numero civico obbligatorio";
      if (!data.immobile.piano?.toString().trim()) return "Piano obbligatorio";
      if (!/^\d{5}$/.test(data.immobile.cap || "")) return "CAP deve avere 5 cifre";
      if (!/^201\d{2}$/.test(data.immobile.cap || ""))
        return "L'indirizzo è fuori dalle zone di Milano. Il CAP deve iniziare con 201.";
      if ((data.immobile.citta || "").trim().toLowerCase() !== "milano")
        return "Solo immobili nel Comune di Milano sono ammessi";
      if ((data.immobile.provincia || "").trim().toUpperCase() !== "MI")
        return "Solo immobili in provincia di Milano (MI) sono ammessi";
      return null;
    case 5:
      if (!data.catasto.foglio?.trim()) return "Foglio obbligatorio";
      if (!data.catasto.particella?.trim()) return "Particella obbligatoria";
      if (!data.catasto.subalterno?.trim()) return "Subalterno obbligatorio";
      if (!(Number(data.catasto.mq_appartamento) > 0))
        return "MQ appartamento obbligatorio (deve essere maggiore di 0)";
      {
        const perts = data.catasto.pertinenze || [];
        for (let i = 0; i < perts.length; i++) {
          const p = perts[i];
          if (!p.foglio?.trim() || !p.particella?.trim() || !p.subalterno?.trim())
            return `Pertinenza #${i + 1}: compila Foglio, Particella e Subalterno (oppure rimuovila)`;
          if (!(Number(p.mq) > 0))
            return `Pertinenza #${i + 1}: la metratura (mq) è obbligatoria e deve essere maggiore di 0`;
        }
        const inc = data.catasto.pertinenze_incluse || [];
        for (let i = 0; i < inc.length; i++) {
          const p = inc[i];
          if (!(Number(p.mq) > 0))
            return `Pertinenza inclusa #${i + 1}: la metratura (mq) è obbligatoria e deve essere maggiore di 0`;
        }
      }
      return null;
    case 6:
      if (!data.contratto.tipologia) return "Seleziona la tipologia";
      if (data.contratto.tipologia !== "transitorio" && !data.contratto.durata_valore)
        return "Seleziona la durata del contratto";
      if (!data.contratto.data_inizio) return "Data inizio obbligatoria";
      if (!data.contratto.data_fine) return "Data fine obbligatoria";
      if (new Date(data.contratto.data_fine) <= new Date(data.contratto.data_inizio))
        return "La data finale deve essere successiva a quella iniziale";
      if (data.contratto.tipologia === "transitorio") {
        // Enforce 1-month to 18-month range for the free-date transitorio flow
        const months = Number(data.contratto.durata_valore) || 0;
        const days = Number(data.contratto.durata_giorni) || 0;
        if (months < 1) return "La durata minima del contratto transitorio è di 1 mese";
        if (months > 18 || (months === 18 && days > 0))
          return "La durata massima del contratto transitorio è di 18 mesi";
      }
      return null;
    case 8: {
      const tot = Number(data.superfici.superficie_totale) || 0;
      if (tot <= 0) return "Superficie totale obbligatoria (> 0)";
      return null;
    }
    case 9:
      // Canone mensile ora si inserisce nello Step 10 (Risultato Calcolo) — nessuna validazione qui.
      return null;
    case 10: {
      const canone = Number(data.step9?.canone_convenuto);
      if (!canone || canone <= 0) return "Inserisci il canone MENSILE richiesto (escluse spese) qui sotto";
      if (calculation && calculation.canone_massimo_mensile != null) {
        if (canone > Number(calculation.canone_massimo_mensile)) {
          return `Il canone richiesto (€ ${canone}) supera il massimo consentito (€ ${calculation.canone_massimo_mensile}). Non è possibile proseguire.`;
        }
      }
      if (!data.manleva_accettata) return "Devi accettare l'informativa e manleva";
      return null;
    }
    case 11: {
      const required = ["visura_catastale", "planimetria_catastale", "ape", "contratto_locazione",
        "scheda_iscrizione", "documento_cf_locatore", "documento_cf_conduttore",
        "ricevuta_bonifico", "ricevuta_bonifico_sindacato"];
      if (data.contratto?.tipologia === "transitorio") required.push("doc_transitorieta");
      if (data.contratto?.tipologia === "studenti") required.push("doc_studenti");
      // Requirement 1 — per ogni pertinenza con visura separata e dati catastali completi
      // servono visura e planimetria dedicate.
      const separatePertinenze = (data.catasto?.pertinenze || []).filter(
        (p) =>
          String(p?.foglio || "").trim() &&
          String(p?.particella || "").trim() &&
          String(p?.subalterno || "").trim()
      );
      separatePertinenze.forEach((p, i) => {
        const id = p.id || i;
        required.push(`visura_pertinenza_${id}`);
        required.push(`planimetria_pertinenza_${id}`);
      });
      const uploaded = new Set((files || []).map((f) => f.doc_type));
      const missing = required.filter((r) => !uploaded.has(r));
      if (missing.length) return `Caricare tutti i documenti (${missing.length} mancanti)`;
      if (uploaded.has("visura_catastale") && !data.visura_mq_ok) {
        return "Confermare che la visura riporta l'indicazione dei mq";
      }
      if (uploaded.has("ape")) {
        if (!data.ape_scadenza) return "Indicare la data di scadenza dell'APE";
        const today = new Date().toISOString().slice(0, 10);
        if (data.ape_scadenza < today) return "APE scaduto: non è possibile proseguire con la pratica";
      }
      return null;
    }
    case 12:
      // Firma del locatore e del conduttore vengono acquisite dopo
      // l'approvazione della pratica da parte di Unioncasa Milano
      // (status `contratto_approvato`). Al submit dello Step 12 non è
      // più richiesta alcuna firma: la pratica viene inviata senza firme
      // e l'utente le completerà dal pannello riservato una volta ricevuto
      // l'OK dell'amministrazione.
      return null;
    default:
      return null;
  }
}

function FormWizard() {
  const { data, setData, step, setStep, files, setFiles, submissionId, saveDraft, isSaving, resetForm, restrictedSteps } = useForm();
  const [calculation, setCalculation] = useState(null);
  const [loadingCalc, setLoadingCalc] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);

  // When the logged-in user is an Agenzia, Step 1 "Richiedente" is useless:
  // we auto-fill `data.richiedente` from the agency profile and skip to Step 2.
  // The server also enforces this on POST /submissions (see
  // _apply_agenzia_richiedente), but prefilling the client state avoids a
  // flash of the empty Step 1 UI.
  const [isAgenziaUser, setIsAgenziaUser] = useState(false);
  useEffect(() => {
    const token = localStorage.getItem("cc_user_token");
    if (!token) return;
    const accType = (localStorage.getItem("cc_user_account_type") || "").toLowerCase();
    if (accType !== "agenzia") return;
    setIsAgenziaUser(true);
    const email = localStorage.getItem("cc_user_email") || "";
    const agenziaNome = localStorage.getItem("cc_user_agenzia_nome") || "";
    const referente = localStorage.getItem("cc_user_nome") || "";
    setData((prev) => ({
      ...prev,
      richiedente: {
        ...(prev.richiedente || {}),
        tipo: "agenzia",
        email: email || prev?.richiedente?.email || "",
        agenzia_nome: agenziaNome || prev?.richiedente?.agenzia_nome || "",
        agenzia_referente: referente || prev?.richiedente?.agenzia_referente || "",
      },
    }));
    // If the user lands on Step 1, jump straight to Step 2 — nothing to fill.
    if (step === 1) setStep(2);
    // eslint-disable-next-line
  }, []);

  // Keep agency users out of Step 1 even if they click back on the sidebar
  useEffect(() => {
    if (isAgenziaUser && step === 1) setStep(2);
  }, [isAgenziaUser, step, setStep]);

  // Restricted-edit mode: when the admin only authorised certain steps,
  // the form must open directly on the first allowed step. If the user
  // somehow lands on a locked step, bounce them to the nearest allowed.
  const isRestrictedEdit = Array.isArray(restrictedSteps) && restrictedSteps.length > 0;
  const allowedStepsSet = isRestrictedEdit
    ? new Set(restrictedSteps.map((n) => Number(n)))
    : null;
  useEffect(() => {
    if (!isRestrictedEdit) return;
    if (!allowedStepsSet.has(step)) {
      // Pick nearest allowed step (first >= current, else highest allowed)
      const sorted = [...allowedStepsSet].sort((a, b) => a - b);
      const next = sorted.find((s) => s >= step) ?? sorted[sorted.length - 1];
      if (next) setStep(next);
    }
    // eslint-disable-next-line
  }, [isRestrictedEdit, step]);

  // Load existing submission files when id changes
  useEffect(() => {
    (async () => {
      if (!submissionId) return;
      try {
        const r = await api.get(`/submissions/${submissionId}`);
        // Allow editing when the submission is either a fresh draft or when
        // the admin has requested integrations/corrections (documenti_mancanti).
        if (r.data.status !== "bozza" && r.data.status !== "documenti_mancanti") {
          resetForm();
          return;
        }
        setFiles(r.data.files || []);
      } catch (err) {
        // Submission not found anymore (DB cleared / deleted) — start fresh
        if (err?.response?.status === 404) {
          resetForm();
        }
      }
    })();
    // eslint-disable-next-line
  }, [submissionId]);

  // When reaching step 10 (Risultato Calcolo), compute calculation
  useEffect(() => {
    if (step !== 10) return;
    (async () => {
      setLoadingCalc(true);
      try {
        const r = await api.post("/calculate", { data });
        setCalculation(r.data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoadingCalc(false);
      }
    })();
    // eslint-disable-next-line
  }, [step]);

  const _pickNextAllowed = (fromStep, dir) => {
    if (!isRestrictedEdit) return Math.max(1, Math.min(12, fromStep + dir));
    const sorted = [...allowedStepsSet].sort((a, b) => a - b);
    if (dir > 0) {
      const n = sorted.find((s) => s > fromStep);
      return n ?? Math.min(12, fromStep + 1);
    }
    const prev = [...sorted].reverse().find((s) => s < fromStep);
    return prev ?? sorted[0];
  };

  const goNext = async () => {
    const err = validateStep(step, data, files, calculation);
    if (err) {
      toast.error(err);
      return;
    }
    // Create/save submission after step 2
    if (step === 2) {
      try { await saveDraft(); } catch (e) { toast.error(formatApiError(e)); return; }
    }
    // Autosave on other steps
    if (step >= 2 && submissionId) {
      try { await saveDraft(); } catch { /* ignore autosave errors */ }
    }
    setStep(_pickNextAllowed(step, +1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    // Agenzia users have Step 1 auto-skipped; keep them at Step 2 minimum
    const minStep = isAgenziaUser ? 2 : 1;
    setStep(Math.max(minStep, _pickNextAllowed(step, -1)));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSaveDraft = async () => {
    if (step === 1) {
      toast.info("Compila almeno lo step 2 prima di salvare");
      return;
    }
    try {
      await saveDraft();
      toast.success("Bozza salvata");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const [emailWarning, setEmailWarning] = useState(null);

  const handleSubmit = async () => {
    const err = validateStep(12, data, files, calculation);
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      await saveDraft();
      const r = await api.post(`/submissions/${submissionId}/submit`);
      // Detect email delivery failures and warn user (submission itself succeeded)
      const es = r?.data?.email_status || {};
      // admin/locatore can be: null (dispatch never ran) | {ok:false,error} | {ok:true}
      const locOk = es?.locatore && es.locatore.ok === true;
      const admOk = es?.admin && es.admin.ok === true;
      if (!locOk || !admOk || es?.pdf_error) {
        const bits = [];
        if (!admOk) bits.push("notifica interna a UnionCasa");
        if (!locOk) bits.push("email di conferma al locatore");
        if (es?.pdf_error) bits.push("la generazione del PDF di attestazione");
        setEmailWarning(
          `La richiesta è stata registrata correttamente, ma non è stato possibile completare ${bits.join(
            " e ",
          )}. UnionCasa Milano ti contatterà comunque per completare la pratica.`,
        );
      }
      // Clear cache immediately so next visit starts fresh
      // (success screen still shows via React state)
      localStorage.removeItem("cc_form_data");
      localStorage.removeItem("cc_submission_id");
      localStorage.removeItem("cc_edit_mode");
      setCompleted(true);
      toast.success("Richiesta inviata con successo!");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (completed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div className="max-w-lg text-center bg-white border border-slate-200 rounded-lg p-10 shadow-sm" data-testid="submission-success">
          <div className="mx-auto w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-5">
            <CheckCircle2 size={28} className="text-green-800" />
          </div>
          <h2 className="font-serif text-3xl text-slate-900 mb-3">Richiesta inviata</h2>
          <p className="text-slate-600 leading-relaxed mb-6">
            La tua richiesta di Attestazione Canone Concordato è stata ricevuta. Verrà
            elaborata da UnionCasa Milano e riceverai aggiornamenti all&apos;indirizzo email del
            locatore.
          </p>
          {emailWarning && (
            <div
              className="text-left border border-amber-300 bg-amber-50 rounded-md p-4 mb-5 text-sm text-amber-900"
              data-testid="email-delivery-warning"
            >
              <p className="font-medium mb-1">⚠ Notifica email non recapitata</p>
              <p className="leading-relaxed">{emailWarning}</p>
            </div>
          )}
          <div className="text-xs text-slate-400 mb-5">
            ID richiesta: <span className="font-mono text-slate-700">{submissionId}</span>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setCompleted(false);
            }}
            variant="outline"
            data-testid="new-submission-btn"
          >
            Compila una nuova richiesta
          </Button>
        </div>
      </div>
    );
  }

  const StepComponent = [
    Step1Intro, Step2Locatore, Step3Conduttore, Step4Zona, Step5Catasto,
    Step6Contratto, Step7Valutazione, Step8Superfici, Step9Maggiorazioni,
    null, Step10Documents, Step12Declaration,
  ][step - 1];

  return (
    <div className="min-h-screen bg-slate-50">
      <ProgressSidebar />

      <main className="lg:ml-[28rem] min-h-screen flex flex-col">
        {/* Sticky top progress bar — bold & prominent */}
        <div className="sticky top-0 z-20 bg-white border-b-2 border-slate-200 shadow-sm" data-testid="top-progress">
          <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-16 pt-5 pb-4">
            <div className="flex items-center gap-3 mb-3">
              {/* Mobile-only hamburger trigger */}
              <MobileNavDrawer />

              {/* Numbered step badge */}
              <div
                className="shrink-0 w-12 h-12 rounded-full bg-slate-900 text-white flex items-center justify-center font-serif text-xl font-medium shadow-md"
                data-testid="top-progress-badge"
              >
                {step}
              </div>
              <div className="min-w-0 flex-1">
                <div className="overline text-slate-500 mb-0.5">
                  Step {step} di {STEP_TITLES.length}
                </div>
                <h3
                  className="font-serif text-lg md:text-xl text-slate-900 truncate leading-tight"
                  data-testid="top-progress-title"
                >
                  {STEP_TITLES[step - 1]}
                </h3>
              </div>
              <div
                className="shrink-0 text-right"
                data-testid="top-progress-percent-wrapper"
              >
                <div
                  className="font-serif text-2xl md:text-3xl font-light text-green-800 tabular-nums leading-none"
                  data-testid="top-progress-percent"
                >
                  {Math.round((step / STEP_TITLES.length) * 100)}%
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">
                  completato
                </div>
              </div>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden ring-1 ring-slate-200">
              <div
                className="h-full rounded-full transition-all duration-700 ease-out relative"
                style={{
                  width: `${(step / STEP_TITLES.length) * 100}%`,
                  background: "linear-gradient(90deg, #14532d 0%, #166534 50%, #16a34a 100%)",
                  boxShadow: "0 0 12px rgba(22, 101, 52, 0.45)",
                }}
                data-testid="top-progress-bar-fill"
              >
                <div className="absolute inset-0 bg-gradient-to-t from-transparent via-white/20 to-white/40 rounded-full" />
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-16 flex-1 w-full">
          {localStorage.getItem("cc_edit_mode") === "1" && (
            <div className="mb-6 border border-orange-300 bg-orange-50 rounded-md p-3 flex items-start gap-2" data-testid="form-edit-mode-banner">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-orange-800 mt-0.5 shrink-0"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
              <div className="flex-1">
                <p className="text-sm text-orange-900 font-medium">Stai modificando una pratica esistente</p>
                <p className="text-xs text-orange-800 mt-0.5">
                  Le modifiche verranno salvate sulla pratica originale.
                  Al termine reinvia la pratica per notificare gli uffici Unioncasa.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    localStorage.removeItem("cc_edit_mode");
                    localStorage.removeItem("cc_submission_id");
                    resetForm();
                    window.location.reload();
                  }}
                  className="mt-2 text-[11px] text-orange-800 underline hover:text-orange-900"
                  data-testid="form-edit-mode-exit"
                >
                  Annulla modifiche e ricomincia da capo
                </button>
              </div>
            </div>
          )}
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25 }}
            >
              {step === 10 ? (
                <Step11Result calculation={calculation} loadingCalc={loadingCalc} />
              ) : StepComponent ? (
                <StepComponent />
              ) : null}
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="mt-12 pt-6 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3">
            <div>
              {step > 1 && (
                <Button
                  variant="ghost"
                  onClick={goBack}
                  className="text-slate-600 hover:text-slate-900"
                  data-testid="btn-back"
                >
                  <ChevronLeft size={16} className="mr-1" /> Indietro
                </Button>
              )}
            </div>
            <div className="flex gap-3 items-center flex-wrap">
              {step > 1 && (
                <Button
                  variant="outline"
                  onClick={handleSaveDraft}
                  disabled={isSaving}
                  data-testid="btn-save-draft"
                >
                  <Save size={16} className="mr-2" />
                  {isSaving ? "Salvataggio..." : "Salva bozza"}
                </Button>
              )}
              {step < 12 ? (
                <Button
                  onClick={goNext}
                  className="bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="btn-next"
                >
                  Continua <ChevronRight size={16} className="ml-1" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-green-800 hover:bg-green-900 text-white"
                  data-testid="btn-submit"
                >
                  <Send size={16} className="mr-2" />
                  {submitting ? "Invio in corso..." : "Invia richiesta"}
                </Button>
              )}
            </div>
          </div>

          <p className="text-xs text-slate-400 mt-6 text-center">
            I dati sono salvati localmente per il recupero della bozza
          </p>
        </div>

        {/* Centered legal footer pinned at bottom */}
        <footer className="border-t border-slate-200 bg-white mt-auto" data-testid="legal-footer">
          <div className="max-w-4xl mx-auto px-6 py-6 text-center">
            <p className="text-xs text-slate-500 leading-relaxed">
              D.M. 16/01/2017 — Attestazione di rispondenza di contratto a canone concordato.
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
}

export default function FormPage() {
  return (
    <FormProvider>
      <FormWizard />
      <PrivacyBanner />
    </FormProvider>
  );
}
